import CoveragePackageModalLink from '../CoveragePackageModalLink';
import { Box, GridItem, SelectField } from '@flos/react-ui';
import { useHippoText } from '../../../../hooks/useHippoText';
import { useState, type ChangeEvent } from 'react';
import { useUpdateCoveragePackageInformation } from '../../../../recoil/hooks/useUpdateCoveragePackageInformation';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { getOnsaCurrentProductSelector } from '../../../../recoil/selectors/getOnsaCurrentProductSelector';
import { useUpdateCoverableInformation } from '../../../../recoil/hooks/useUpdateCoverableInformation';
import { allCoverablesOnsaAtom } from '../../../../recoil/atoms/allCoverablesOnsaAtom';
import {
    formatStringToMonetaryValueString,
    getOnsaCoverable
} from '../../../../utils/coverablesUtil';
import { refreshCoveragePackagePricesAtom } from '../../../../recoil/atoms/refreshCoveragePackagePricesAtom';
import type { CaravanOnsaDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/onsa/uvp';
import { deductibleOptionsSelector } from '../../../../recoil/selectors/deductibleOptionsSelector';
import { caravanContentSumOptionsSelector } from '../../../../recoil/selectors/caravanContentSumOptionsSelector';
import CoveragePackageUvpInputs from './CoveragePackageUvpInputs';
import { UVPCOMPREHENSIVE_COV } from '../../../../constants/coverage';

const CoveragePackageCaravanInputs = ({
    isLoading,
    setIsLoading
}: {
    isLoading: boolean;
    setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
}): React.JSX.Element => {
    const getHippoText = useHippoText();
    const modalName = getHippoText('DSS-COVERAGE-PACKAGE-PAGE-INPUT-INFO');
    const { updateCoverableInformation } = useUpdateCoverableInformation();
    const { updateCoveragePackageInformation } =
        useUpdateCoveragePackageInformation<CaravanOnsaDTO>();
    const { flavourName = '', fixedId = '' } = useRecoilValue(getOnsaCurrentProductSelector);
    const coverables = useRecoilValue(allCoverablesOnsaAtom);
    const coverable = getOnsaCoverable(coverables, flavourName, fixedId) as CaravanOnsaDTO;
    const refreshPrices = useSetRecoilState(refreshCoveragePackagePricesAtom);
    const sumOptions = useRecoilValue(caravanContentSumOptionsSelector);
    const deductibleOptions = useRecoilValue(deductibleOptionsSelector(UVPCOMPREHENSIVE_COV));
    const [deductible, setDeductible] = useState(coverable.deductible);
    const [contentSum, setContentSum] = useState(coverable.contentSum);

    const changeSum = (event: ChangeEvent<HTMLSelectElement>): void => {
        const sum = event.target.value;
        setIsLoading(true);
        setContentSum(sum);
        const option = sumOptions.find((elt) => elt.optionCode === sum);
        if (!option) {
            setIsLoading(false);
            return;
        }
        updateCoverableInformation({ contentSum: option.optionCode });
        void updateCoveragePackageInformation({
            contentSum: option.optionCode
        }).then(() => {
            refreshPrices({});
            setIsLoading(false);
        });
    };

    return (
        <GridItem span={7}>
            <Box>
                <CoveragePackageUvpInputs
                    isLoading={isLoading}
                    setIsLoading={setIsLoading}
                    deductibleOptions={deductibleOptions}
                    deductible={deductible}
                    setDeductible={setDeductible}
                    updateCoveragePackageInformation={updateCoveragePackageInformation}
                ></CoveragePackageUvpInputs>
                <div className="u-flex-box u-flex-box--space-between">
                    <h4>{getHippoText('DSS-COVERAGE-PACKAGE-PAGE-UVP-CARVAN-SUM-INPUT-HEADER')}</h4>
                    <CoveragePackageModalLink
                        modalName={modalName}
                        title={getHippoText('DSS-INFO-MODAL-UVPLOB-CONTENT-SUM-TITLE')}
                        description={getHippoText('DSS-INFO-MODAL-UVPLOB-CONTENT-SUM-BODY')}
                    />
                </div>
                <div className="u-spacing-stack-none">
                    <SelectField
                        label={getHippoText('DSS-COVERAGE-PACKAGE-PAGE-UVP-CARVAN-SUM-INPUT')}
                        onChange={changeSum}
                        options={sumOptions.map((option) => {
                            return {
                                label: formatStringToMonetaryValueString(option.displayName),
                                value: option.optionCode
                            };
                        })}
                        value={contentSum}
                        disabled={isLoading}
                    ></SelectField>
                </div>
            </Box>
        </GridItem>
    );
};

export default CoveragePackageCaravanInputs;
